import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Typography, Table, Tag, Select, DatePicker, Space, Spin, Empty,
  Card, Collapse, Tooltip, message, Button,
} from 'antd';
import ResizableTitle from '../components/ResizableTitle';
import { DownloadOutlined } from '@ant-design/icons';
import * as XLSX from 'xlsx';
import dayjs from 'dayjs';
import api from '../api';

import { selectFilter } from '../utils/selectFilter';
const { RangePicker } = DatePicker;

// ── helpers ───────────────────────────────────────────────────────────────────

function pct(earned, max) {
  return max > 0 ? Math.round((earned / max) * 1000) / 10 : null;
}

function scoreColor(p) {
  if (p == null) return '#bfbfbf';
  if (p >= 90)   return '#52c41a';
  if (p >= 70)   return '#fa8c16';
  return '#ff4d4f';
}

function ScoreCell({ earned, max, count }) {
  const p = pct(earned, max);
  if (p == null && count === 0) return <span style={{ color: '#bfbfbf' }}>—</span>;
  return (
    <span style={{ color: scoreColor(p), fontWeight: 500 }}>
      {p != null ? `${p}%` : '—'}
      <br />
      <small style={{ color: '#8c8c8c', fontWeight: 400, fontSize: 11 }}>({count})</small>
    </span>
  );
}

function OverallCell({ rows }) {
  const earned = rows.reduce((s, r) => s + r.earned, 0);
  const max    = rows.reduce((s, r) => s + r.max,    0);
  const count  = rows.reduce((s, r) => s + r.count,  0);
  return <ScoreCell earned={earned} max={max} count={count} />;
}

// ── pivot logic ───────────────────────────────────────────────────────────────

function pivot(rows) {
  // evaluators list (ordered)
  const evalOrder = [];
  const evalSeen  = new Set();

  // section → evaluator → { earned, max, count }
  const secMap  = {};
  // section → question → evaluator → { earned, max, count }
  const qMap    = {};

  rows.forEach((r) => {
    if (!evalSeen.has(r.EvaluatorName)) { evalSeen.add(r.EvaluatorName); evalOrder.push(r.EvaluatorName); }

    // section aggregation
    if (!secMap[r.SectionName])                           secMap[r.SectionName]                           = {};
    if (!secMap[r.SectionName][r.EvaluatorName])          secMap[r.SectionName][r.EvaluatorName]          = { earned: 0, max: 0, count: 0 };
    secMap[r.SectionName][r.EvaluatorName].earned += r.TotalEarned;
    secMap[r.SectionName][r.EvaluatorName].max    += r.TotalMax;
    secMap[r.SectionName][r.EvaluatorName].count  += r.AnswerCount;

    // question aggregation
    const qKey = `${r.SectionName}||${r.QuestionText}`;
    if (!qMap[qKey]) qMap[qKey] = { section: r.SectionName, question: r.QuestionText, byEval: {} };
    qMap[qKey].byEval[r.EvaluatorName] = {
      earned: r.TotalEarned, max: r.TotalMax, count: r.AnswerCount,
    };
  });

  return { evalOrder, secMap, qMap };
}

// ── columns builder ───────────────────────────────────────────────────────────

function buildColumns(evalNames, labelTitle, labelKey, getCell, getOverallRows) {
  return [
    {
      title:     labelTitle,
      dataIndex: labelKey,
      key:       labelKey,
      width:     220,
      ellipsis:  true,
      render:    (v) => <Tooltip title={v}><span>{v}</span></Tooltip>,
    },
    ...evalNames.map((name) => ({
      title:     <Tooltip title={name}><span style={{ fontSize: 12 }}>{name}</span></Tooltip>,
      key:       name,
      width:     110,
      align:     'center',
      render:    (_, record) => {
        const cell = getCell(record, name);
        if (!cell) return <span style={{ color: '#bfbfbf' }}>—</span>;
        return <ScoreCell earned={cell.earned} max={cell.max} count={cell.count} />;
      },
    })),
    {
      title:  <span style={{ fontWeight: 700 }}>Overall</span>,
      key:    'overall',
      width:  100,
      align:  'center',
      render: (_, record) => <OverallCell rows={getOverallRows(record)} />,
    },
  ];
}

// ── page ──────────────────────────────────────────────────────────────────────

export default function EvalQAComparisonPage() {
  const defaultStart = dayjs().startOf('month');
  const defaultEnd   = dayjs();

  const [colWidths, setColWidths] = useState({});

  const toResizable = (col) => ({
    ...col,
    width: colWidths[col.key] ?? col.width ?? 120,
    onHeaderCell: (column) => ({
      width: column.width,
      onResize: (_, { size }) => setColWidths((p) => ({ ...p, [col.key]: size.width })),
    }),
  });

  const [dateRange,    setDateRange]    = useState([defaultStart, defaultEnd]);
  const [campaign,     setCampaign]     = useState('');
  const [formFilter,   setFormFilter]   = useState([]);   // server-side form filter
  const [evalFilter,   setEvalFilter]   = useState([]);   // client-side evaluator filter

  const [agentFilter,  setAgentFilter]  = useState([]);   // server-side agent filter

  const [rawRows,      setRawRows]      = useState([]);
  const [loading,      setLoading]      = useState(false);
  const [campaignOpts, setCampaignOpts] = useState([]);
  const [formOpts,     setFormOpts]     = useState([]);
  const [agentOpts,    setAgentOpts]    = useState([]);

  // Load campaign options whenever the date range changes
  const loadCampaigns = useCallback(async () => {
    try {
      const res = await api.get('/api/eval/campaigns', {
        params: {
          call_date_start: dateRange[0]?.format('YYYY-MM-DD'),
          call_date_end:   dateRange[1]?.format('YYYY-MM-DD'),
        },
      });
      setCampaignOpts(res.data);
    } catch { /* silent — campaign filter just stays empty */ }
  }, [dateRange]);

  useEffect(() => { loadCampaigns(); }, [loadCampaigns]);

  // Load form + agent options once
  useEffect(() => {
    api.get('/api/eval-forms/names')
      .then((r) => setFormOpts(r.data.map((n) => ({ label: n, value: n }))))
      .catch(() => {});
    api.get('/api/agent-stats/options')
      .then((r) => setAgentOpts((r.data.agents || []).map((a) => ({ label: a.name, value: a.id ?? a.name }))))
      .catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        call_date_start: dateRange[0]?.format('YYYY-MM-DD'),
        call_date_end:   dateRange[1]?.format('YYYY-MM-DD'),
      };
      if (campaign)            params.campaigns   = campaign;
      if (formFilter.length)   params.form_names  = formFilter.join(',');
      const agentIds   = agentFilter.filter((v) => typeof v === 'number');
      const agentNames = agentFilter.filter((v) => typeof v === 'string');
      if (agentIds.length)   params.agent_user_id = agentIds.join(',');
      if (agentNames.length) params.agent_name    = agentNames.join(',');
      const res = await api.get('/api/eval/qa-comparison', { params });
      setRawRows(res.data);
    } catch { message.error('Failed to load comparison data.'); }
    finally { setLoading(false); }
  }, [dateRange, campaign, formFilter, agentFilter]);

  useEffect(() => { load(); }, [load]);

  // Apply client-side evaluator filter
  const rows = useMemo(() =>
    evalFilter.length > 0
      ? rawRows.filter((r) => evalFilter.includes(r.EvaluatorName))
      : rawRows,
    [rawRows, evalFilter]
  );

  const { evalOrder, secMap, qMap } = useMemo(() => pivot(rows), [rows]);

  // All evaluator names (for the multi-select options)
  const allEvalNames = useMemo(() => {
    const s = new Set(rawRows.map((r) => r.EvaluatorName));
    return [...s].sort();
  }, [rawRows]);

  // Sections list (preserving insert order from pivot)
  const sections = Object.keys(secMap);

  // ── Section summary table ──────────────────────────────────────────────────

  const secTableData = sections.map((sec) => ({
    key:     sec,
    section: sec,
    byEval:  secMap[sec],
  }));

  const secColumns = buildColumns(
    evalOrder,
    'Section',
    'section',
    (record, name) => record.byEval?.[name],
    (record) => Object.values(record.byEval || {}),
  );

  // ── Question breakdown (per section) ──────────────────────────────────────

  const questionsBySec = useMemo(() => {
    const out = {};
    Object.values(qMap).forEach((q) => {
      if (!out[q.section]) out[q.section] = [];
      out[q.section].push(q);
    });
    return out;
  }, [qMap]);

  const qColumns = buildColumns(
    evalOrder,
    'Question',
    'question',
    (record, name) => record.byEval?.[name],
    (record) => Object.values(record.byEval || {}),
  );

  // ── Export helpers ─────────────────────────────────────────────────────────

  function cellText(cell) {
    if (!cell || cell.count === 0) return '—';
    const p = pct(cell.earned, cell.max);
    return p != null ? `${p}% (${cell.count})` : `— (${cell.count})`;
  }

  function overallText(byEval) {
    const vals = Object.values(byEval || {});
    const earned = vals.reduce((s, r) => s + r.earned, 0);
    const max    = vals.reduce((s, r) => s + r.max,    0);
    const count  = vals.reduce((s, r) => s + r.count,  0);
    if (count === 0) return '—';
    const p = pct(earned, max);
    return p != null ? `${p}% (${count})` : `— (${count})`;
  }

  const exportSections = () => {
    const rows = secTableData.map((row) => {
      const out = { Section: row.section };
      evalOrder.forEach((name) => { out[name] = cellText(row.byEval?.[name]); });
      out['Overall'] = overallText(row.byEval);
      return out;
    });
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Section Scores');
    XLSX.writeFile(wb, 'qa-comparison-sections.xlsx');
  };

  const exportQuestions = () => {
    const rows = [];
    sections.forEach((sec) => {
      (questionsBySec[sec] || []).forEach((q) => {
        const out = { Section: sec, Question: q.question };
        evalOrder.forEach((name) => { out[name] = cellText(q.byEval?.[name]); });
        out['Overall'] = overallText(q.byEval);
        rows.push(out);
      });
    });
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Question Breakdown');
    XLSX.writeFile(wb, 'qa-comparison-questions.xlsx');
  };

  return (
    <>
      {/* Filters */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 20, alignItems: 'center' }}>
        <Typography.Title level={4} style={{ margin: 0, flex: '1 1 auto' }}>
          QA Team Comparison
        </Typography.Title>
        <Space wrap>
          <RangePicker
            value={dateRange}
            onChange={(v) => setDateRange(v ?? [defaultStart, defaultEnd])}
            format="YYYY-MM-DD"
          />
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder="All campaigns"
            value={campaign || undefined}
            onChange={(v) => { setCampaign(v || ''); setFormFilter([]); }}
            options={campaignOpts.map((c) => ({ label: c.Name, value: c.ID }))}
            style={{ minWidth: 220 }}
          />
          <Select
            mode="multiple"
            allowClear
            showSearch filterOption={selectFilter}
            placeholder="All forms"
            value={formFilter}
            onChange={setFormFilter}
            options={formOpts}
            style={{ minWidth: 240 }}
            maxTagCount="responsive"
          />
          <Select
            mode="multiple"
            allowClear
            showSearch filterOption={selectFilter}
            placeholder="All agents"
            value={agentFilter}
            onChange={setAgentFilter}
            options={agentOpts}
            style={{ minWidth: 220 }}
            maxTagCount="responsive"
          />
          <Select
            mode="multiple"
            allowClear
            placeholder="All evaluators"
            value={evalFilter}
            onChange={setEvalFilter}
            options={allEvalNames.map((n) => ({ label: n, value: n }))}
            style={{ minWidth: 220 }}
            maxTagCount="responsive"
          />
        </Space>
      </div>

      {loading ? (
        <Spin style={{ display: 'block', margin: '60px auto' }} />
      ) : rows.length === 0 ? (
        <Empty description="No completed evaluations found for the selected filters." />
      ) : (
        <>
          {/* Section summary */}
          <Card
            size="small"
            title={
              <Space>
                <span>Section Scores</span>
                <Typography.Text type="secondary" style={{ fontSize: 12, fontWeight: 400 }}>
                  avg % · (answers)
                </Typography.Text>
              </Space>
            }
            extra={
              <Button size="small" icon={<DownloadOutlined />} onClick={exportSections}>
                Export
              </Button>
            }
            style={{ marginBottom: 24 }}
          >
            <Table
              rowKey="section"
              dataSource={secTableData}
              columns={secColumns.map(toResizable)}
              size="small"
              pagination={false}
              scroll={{ x: true }}
              bordered
              components={{ header: { cell: ResizableTitle } }}
            />
          </Card>

          {/* Question breakdown per section */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
            <Typography.Title level={5} style={{ margin: 0 }}>
              Question Breakdown
            </Typography.Title>
            <Button size="small" icon={<DownloadOutlined />} onClick={exportQuestions}>
              Export
            </Button>
          </div>
          <Collapse
            defaultActiveKey={sections}
            items={sections.map((sec) => ({
              key:   sec,
              label: <Typography.Text strong>{sec}</Typography.Text>,
              children: (
                <Table
                  rowKey="question"
                  dataSource={(questionsBySec[sec] || []).map((q) => ({
                    key:      q.question,
                    question: q.question,
                    byEval:   q.byEval,
                  }))}
                  columns={qColumns.map(toResizable)}
                  size="small"
                  pagination={false}
                  scroll={{ x: true }}
                  bordered
                  components={{ header: { cell: ResizableTitle } }}
                />
              ),
            }))}
          />
        </>
      )}
    </>
  );
}
